/**
 * Processing Time Distribution Chart — Pure HTML/CSS implementation
 *
 * Horizontal bar chart showing P50/P90/P99 per operation.
 * P50 = green, P90 = yellow, P99 = red.
 */

import { useTranslation } from '@/hooks/useTranslation';
import type { OperationTimeStats } from '@/services/metricsAPI';

interface ProcessingTimeChartProps {
  stats: OperationTimeStats[];
}

const formatSeconds = (val: number): string => {
  if (val < 60) return `${val.toFixed(1)}s`;
  return `${Math.floor(val / 60)}m${(val % 60).toFixed(0)}s`;
};

export function ProcessingTimeChart({ stats }: ProcessingTimeChartProps): JSX.Element {
  const { t } = useTranslation();

  const data = stats
    .filter((s) => s.sample_count > 0)
    .slice(0, 10)
    .map((s) => ({
      name: s.operation.replace(/_/g, ' '),
      provider: s.provider,
      p50: s.p50_processing_seconds,
      p90: s.p90_processing_seconds,
      p99: s.p99_processing_seconds,
    }));

  if (data.length === 0) {
    return <div className="chart-empty-state">{t('admin.metrics.noChartData')}</div>;
  }

  const maxVal = Math.max(...data.map((d) => d.p99), 1);

  return (
    <div className="space-y-3">
      {/* Legend */}
      <div className="flex gap-4 text-xs text-white/60 mb-2">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-green-400/80" />
          {t('admin.metrics.chartP50')}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-yellow-400/80" />
          {t('admin.metrics.chartP90')}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-red-400/80" />
          {t('admin.metrics.chartP99')}
        </span>
      </div>

      {data.map((d) => (
        <div key={`${d.name}-${d.provider ?? 'all'}`}>
          {/* Operation name */}
          <div className="text-xs font-medium text-white/70 mb-1 truncate">
            {d.name}
            {d.provider && <span className="text-white/30 ml-1">({d.provider})</span>}
          </div>

          {/* P50 bar */}
          <div className="flex items-center gap-2 mb-0.5">
            <div className="flex-1 h-3 bg-white/5 rounded overflow-hidden">
              <div
                className="h-full bg-green-400/80 rounded transition-all duration-300"
                style={{ width: `${Math.max((d.p50 / maxVal) * 100, 1)}%` }}
              />
            </div>
            <span className="text-[10px] text-white/40 w-14 text-right font-mono">
              {formatSeconds(d.p50)}
            </span>
          </div>

          {/* P90 bar */}
          <div className="flex items-center gap-2 mb-0.5">
            <div className="flex-1 h-3 bg-white/5 rounded overflow-hidden">
              <div
                className="h-full bg-yellow-400/80 rounded transition-all duration-300"
                style={{ width: `${Math.max((d.p90 / maxVal) * 100, 1)}%` }}
              />
            </div>
            <span className="text-[10px] text-white/40 w-14 text-right font-mono">
              {formatSeconds(d.p90)}
            </span>
          </div>

          {/* P99 bar */}
          <div className="flex items-center gap-2">
            <div className="flex-1 h-3 bg-white/5 rounded overflow-hidden">
              <div
                className="h-full bg-red-400/80 rounded transition-all duration-300"
                style={{ width: `${Math.max((d.p99 / maxVal) * 100, 1)}%` }}
              />
            </div>
            <span className="text-[10px] text-white/40 w-14 text-right font-mono">
              {formatSeconds(d.p99)}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}
